import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { axiosInstance } from "../../../utils/axiosInstance";
import Message from "../../Alert/Message";

interface OneUptimeSettingsResponse {
  configured: boolean;
  keyHint: string | null;
  envFallbackConfigured?: boolean;
  baseUrlConfigured?: boolean;
  appBaseUrlConfigured?: boolean;
}

export default function OneUptimeSettings() {
  const [message, setMessage] = useState<string | boolean>(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [status, setStatus] = useState<OneUptimeSettingsResponse | null>(null);

  const load = async () => {
    setFetching(true);
    try {
      const res = await axiosInstance.get<OneUptimeSettingsResponse>(
        "/settings/oneuptime"
      );
      setStatus(res.data);
    } catch (e) {
      console.error(e);
      setMessage("Could not load OneUptime settings.");
    }
    setFetching(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    setMessage(false);
    try {
      await axiosInstance.put("/settings/oneuptime", { apiKey });
      setApiKey("");
      setMessage("API key saved.");
      await load();
    } catch (e: any) {
      console.error(e);
      setMessage(
        e?.response?.data?.message || "Failed to save API key."
      );
    }
    setLoading(false);
  };

  const clearKey = async () => {
    setLoading(true);
    setMessage(false);
    try {
      await axiosInstance.put("/settings/oneuptime", { apiKey: "" });
      setApiKey("");
      setMessage("Stored API key cleared.");
      await load();
    } catch (e: any) {
      console.error(e);
      setMessage(
        e?.response?.data?.message || "Failed to clear API key."
      );
    }
    setLoading(false);
  };

  return (
    <Box className="sq-settings-panel">
      <Message message={message} setMessage={setMessage} />
      <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" component="h2" gutterBottom>
          OneUptime
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Open incidents are loaded from your self-hosted{" "}
          <Link
            href="https://oneuptime.com/docs/en/api-reference/api-reference"
            target="_blank"
            rel="noopener noreferrer"
          >
            OneUptime
          </Link>{" "}
          instance using a project-scoped API key. The key is stored on the
          server and never shown in full after saving. Set{" "}
          <code>ONEUPTIME_BASE_URL</code> and <code>ONEUPTIME_APP_BASE_URL</code>{" "}
          in the backend environment.
        </Typography>
        {fetching ? (
          <Typography variant="body2">Loading…</Typography>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              Key in database:{" "}
              {status?.configured
                ? `configured (${status.keyHint})`
                : "not set"}
            </Typography>
            {status?.envFallbackConfigured ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Server environment variable <code>ONEUPTIME_API_KEY</code> is
                set and can be used when no database key is stored.
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              API base URL:{" "}
              {status?.baseUrlConfigured ? "configured" : "not set on server"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Dashboard URL:{" "}
              {status?.appBaseUrlConfigured
                ? "configured"
                : "not set on server"}
            </Typography>
          </Box>
        )}
        <TextField
          label="Project API key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          fullWidth
          autoComplete="off"
          placeholder="Paste new key to replace stored value"
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={save}
            disabled={loading || fetching}
          >
            Save API key
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={clearKey}
            disabled={loading || fetching || !status?.configured}
          >
            Clear stored key
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
